import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Award, Save, Trophy, User, Plus, Minus, RefreshCw, Loader2 } from "lucide-react";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function LoyaltyPage() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [selectedCust, setSelectedCust] = useState(null);
  const [custDetail, setCustDetail] = useState(null);
  const [adjust, setAdjust] = useState({ delta: 0, reason: "" });

  const load = async () => {
    try {
      const [s, l] = await Promise.all([
        api.get("/loyalty/settings"),
        api.get("/loyalty/leaderboard?limit=50"),
      ]);
      setSettings(s.data);
      setLeaderboard(l.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const clean = {
        enabled: !!settings.enabled,
        earn_rate_amount: Number(settings.earn_rate_amount) || 100,
        earn_rate_points: Number(settings.earn_rate_points) || 1,
        earn_base: settings.earn_base || "subtotal",
        redemption_tiers: (settings.redemption_tiers || []).map((t) => ({
          min_points: Number(t.min_points) || 0,
          discount_pct: Number(t.discount_pct) || 0,
          label: t.label || `${t.discount_pct}% off`,
        })),
        points_expiry_days: settings.points_expiry_days ? Number(settings.points_expiry_days) : null,
      };
      const res = await api.put("/loyalty/settings", clean);
      setSettings(res.data);
      toast.success("Loyalty settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const openCust = async (c) => {
    setSelectedCust(c);
    setAdjust({ delta: 0, reason: "" });
    try {
      const res = await api.get(`/loyalty/customer/${c.id}`);
      setCustDetail(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    }
  };

  const doAdjust = async (sign) => {
    if (!selectedCust || !adjust.delta) return;
    try {
      const res = await api.post("/loyalty/adjust", {
        customer_id: selectedCust.id,
        delta: sign * Number(adjust.delta),
        reason: adjust.reason || "admin adjust",
      });
      toast.success(`New balance: ${res.data.balance}`);
      await openCust(selectedCust);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Adjust failed");
    }
  };

  const addTier = () => setSettings((s) => ({ ...s, redemption_tiers: [...(s.redemption_tiers || []), { min_points: 500, discount_pct: 40, label: "40% off" }] }));
  const removeTier = (i) => setSettings((s) => ({ ...s, redemption_tiers: s.redemption_tiers.filter((_, idx) => idx !== i) }));
  const updateTier = (i, k, v) => setSettings((s) => ({ ...s, redemption_tiers: s.redemption_tiers.map((t, idx) => idx === i ? { ...t, [k]: v } : t) }));

  if (!settings) return <div className="p-8 text-[#8B7355]"><Loader2 className="w-4 h-4 inline animate-spin mr-2" />Loading loyalty…</div>;

  return (
    <div className="p-6 space-y-6" data-testid="loyalty-admin-page">
      <div>
        <div className="uc-label">Loyalty & Rewards</div>
        <h1 className="font-[Outfit] text-3xl sm:text-4xl font-semibold tracking-tight mt-1 flex items-center gap-2">
          <Award className="w-7 h-7 text-[#C05621]" /> Loyalty Program
        </h1>
        <p className="text-sm text-[#8B7355] mt-1">
          Points-based rewards for repeat customers. Fully editable earn rate and redemption tiers.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <Card className="md:col-span-2 border-[#EAE5D9]">
          <CardHeader>
            <CardTitle className="font-[Outfit] text-lg">Program Settings</CardTitle>
            <CardDescription>These rules apply on every POS sale (earn + redeem).</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="flex items-center justify-between rounded-md bg-[#FAF6EE] border border-[#EAE5D9] px-4 py-3">
              <div>
                <div className="font-medium">Program enabled</div>
                <div className="text-xs text-[#8B7355]">Turn off to freeze all earn / redeem operations.</div>
              </div>
              <Switch
                checked={!!settings.enabled}
                onCheckedChange={(v) => setSettings({ ...settings, enabled: v })}
                data-testid="loyalty-enabled-switch"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Spend amount (₹)</Label>
                <Input
                  type="number" min="1"
                  value={settings.earn_rate_amount}
                  onChange={(e) => setSettings({ ...settings, earn_rate_amount: e.target.value })}
                  data-testid="loyalty-earn-amount"
                />
              </div>
              <div>
                <Label>Earns points</Label>
                <Input
                  type="number" min="1"
                  value={settings.earn_rate_points}
                  onChange={(e) => setSettings({ ...settings, earn_rate_points: e.target.value })}
                  data-testid="loyalty-earn-points"
                />
              </div>
              <div>
                <Label>Earn base</Label>
                <Select value={settings.earn_base} onValueChange={(v) => setSettings({ ...settings, earn_base: v })}>
                  <SelectTrigger data-testid="loyalty-earn-base"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="subtotal">Subtotal (excl. GST)</SelectItem>
                    <SelectItem value="grand_total">Grand total (incl. GST)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="rounded-md bg-[#F7F2E8] p-3 text-xs text-[#5C4033]">
              Current rule: Spend <b>{inr(settings.earn_rate_amount)}</b> (on {settings.earn_base === "subtotal" ? "subtotal" : "grand total"})
              &nbsp;→ earn <b>{settings.earn_rate_points} pt{Number(settings.earn_rate_points) > 1 ? "s" : ""}</b>.
            </div>

            <div>
              <Label className="flex items-center justify-between">
                <span>Redemption tiers</span>
                <button onClick={addTier} className="text-xs text-[#C05621] hover:underline" data-testid="loyalty-add-tier">+ add tier</button>
              </Label>
              <div className="mt-2 space-y-2">
                {(settings.redemption_tiers || []).map((t, i) => (
                  <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-end">
                    <div>
                      <div className="text-[10px] text-[#8B7355]">Min points to redeem</div>
                      <Input type="number" value={t.min_points} onChange={(e) => updateTier(i, "min_points", e.target.value)} data-testid={`loyalty-tier-pts-${i}`} />
                    </div>
                    <div>
                      <div className="text-[10px] text-[#8B7355]">Discount %</div>
                      <Input type="number" step="0.1" value={t.discount_pct} onChange={(e) => updateTier(i, "discount_pct", e.target.value)} data-testid={`loyalty-tier-pct-${i}`} />
                    </div>
                    <div>
                      <div className="text-[10px] text-[#8B7355]">Label</div>
                      <Input value={t.label || ""} onChange={(e) => updateTier(i, "label", e.target.value)} data-testid={`loyalty-tier-label-${i}`} />
                    </div>
                    <button onClick={() => removeTier(i)} className="text-xs text-red-600 hover:underline pb-2" data-testid={`loyalty-tier-remove-${i}`}>remove</button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end">
              <Button onClick={save} disabled={saving} className="bg-[#2C1810] text-white" data-testid="loyalty-save-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                Save settings
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border-[#EAE5D9]">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="font-[Outfit] text-lg flex items-center gap-2"><Trophy className="w-4 h-4 text-[#C05621]" /> Top customers</CardTitle>
              <CardDescription>Highest balance now.</CardDescription>
            </div>
            <button onClick={load} className="text-xs text-[#C05621] hover:underline flex items-center gap-1">
              <RefreshCw className="w-3 h-3" /> Refresh
            </button>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-[#EAE5D9] max-h-[520px] overflow-y-auto" data-testid="loyalty-leaderboard">
              {leaderboard.length === 0 && <div className="p-6 text-center text-sm text-[#8B7355]">No customers with points yet.</div>}
              {leaderboard.map((c, i) => (
                <button
                  key={c.id}
                  onClick={() => openCust(c)}
                  className="w-full text-left px-4 py-3 hover:bg-[#FAF6EE] flex items-center justify-between"
                  data-testid={`loyalty-cust-${c.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i < 3 ? "bg-[#C05621] text-white" : "bg-[#EAE5D9] text-[#5C4033]"}`}>{i + 1}</div>
                    <div>
                      <div className="font-medium">{c.name}</div>
                      <div className="text-xs text-[#8B7355]">{c.phone}</div>
                    </div>
                  </div>
                  <Badge className="bg-[#C05621] text-white">{c.loyalty_points} pts</Badge>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={!!selectedCust} onOpenChange={(o) => { if (!o) { setSelectedCust(null); setCustDetail(null); } }}>
        <DialogContent className="max-w-3xl bg-white max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-[Outfit] flex items-center gap-2">
              <User className="w-5 h-5 text-[#C05621]" /> {custDetail?.customer?.name || selectedCust?.name}
            </DialogTitle>
          </DialogHeader>
          {custDetail ? (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <StatBox label="Current balance" value={`${custDetail.customer.loyalty_points} pts`} tone="orange" />
                <StatBox label="Next tier" value={
                  custDetail.next_tier
                    ? `${custDetail.next_tier.points_needed} pts to ${custDetail.next_tier.label}`
                    : "Max tier reached"
                } />
                <StatBox label="Available now" value={custDetail.available_tiers.length ? custDetail.available_tiers.map((t) => t.label).join(", ") : "None"} tone="green" />
              </div>

              <div className="rounded-md bg-[#FAF6EE] border border-[#EAE5D9] p-3">
                <div className="text-xs uppercase text-[#8B7355] mb-2">Manual adjustment</div>
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Label className="text-xs">Points</Label>
                    <Input type="number" value={adjust.delta} onChange={(e) => setAdjust({ ...adjust, delta: e.target.value })} data-testid="loyalty-adjust-delta" />
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs">Reason</Label>
                    <Input value={adjust.reason} placeholder="Goodwill gesture" onChange={(e) => setAdjust({ ...adjust, reason: e.target.value })} data-testid="loyalty-adjust-reason" />
                  </div>
                  <Button onClick={() => doAdjust(+1)} className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="loyalty-adjust-add">
                    <Plus className="w-3 h-3 mr-1" /> Add
                  </Button>
                  <Button onClick={() => doAdjust(-1)} variant="outline" className="border-red-500 text-red-700" data-testid="loyalty-adjust-sub">
                    <Minus className="w-3 h-3 mr-1" /> Deduct
                  </Button>
                </div>
              </div>

              <div>
                <div className="text-xs uppercase text-[#8B7355] mb-2">Point history</div>
                <div className="max-h-[300px] overflow-y-auto rounded-md border border-[#EAE5D9]">
                  <table className="w-full text-sm">
                    <thead className="bg-[#FAF6EE] text-[#8B7355] text-[10px] uppercase">
                      <tr>
                        <th className="text-left px-3 py-2">When</th>
                        <th className="text-left px-3 py-2">Reason</th>
                        <th className="text-left px-3 py-2">Ref</th>
                        <th className="text-right px-3 py-2">Δ</th>
                        <th className="text-right px-3 py-2">Balance</th>
                      </tr>
                    </thead>
                    <tbody data-testid="loyalty-ledger-body">
                      {custDetail.ledger.map((l) => (
                        <tr key={l.id} className="border-t border-[#EAE5D9]">
                          <td className="px-3 py-2 text-xs text-[#8B7355]">{new Date(l.at).toLocaleString("en-IN")}</td>
                          <td className="px-3 py-2 text-xs uppercase">{l.reason}</td>
                          <td className="px-3 py-2 text-xs font-mono">{l.sale_number || l.meta?.note || "—"}</td>
                          <td className={`px-3 py-2 text-right font-semibold ${l.delta >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                            {l.delta > 0 ? "+" : ""}{l.delta}
                          </td>
                          <td className="px-3 py-2 text-right">{l.balance_after}</td>
                        </tr>
                      ))}
                      {custDetail.ledger.length === 0 && (
                        <tr><td colSpan={5} className="px-3 py-8 text-center text-[#8B7355]">No history yet.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-6 text-center"><Loader2 className="w-4 h-4 inline animate-spin" /> Loading…</div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setSelectedCust(null); setCustDetail(null); }}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatBox({ label, value, tone = "default" }) {
  const cls = tone === "orange" ? "text-[#C05621]" : tone === "green" ? "text-emerald-700" : "text-[#2C1810]";
  return (
    <div className="border border-[#EAE5D9] rounded-md p-3 bg-white">
      <div className="text-[10px] uppercase tracking-wider text-[#8B7355]">{label}</div>
      <div className={`text-lg font-[Outfit] font-semibold ${cls}`}>{value}</div>
    </div>
  );
}
